import type { TTokenLimitPeriod } from "../types/organizations";
import type { IGetTokenUsageSummaryResponse } from "../types/token-usage";
import { ETokenUsageEndpointType } from "../types/token-usage";

// NOTE: Rendered in the summary tiles when the table is forced to zero
// results (user unselected every type or endpoint). Keeps the cards in
// sync with the empty list instead of showing unfiltered global totals.
export const EMPTY_TOKEN_USAGE_SUMMARY: IGetTokenUsageSummaryResponse = {
  totalRecords: 0,
  totalInputTokens: 0,
  totalOutputTokens: 0,
  totalReasoningTokens: 0,
  totalCachedInputTokens: 0,
  totalCacheWriteInputTokens: 0,
  totalTokens: 0,
  totalDurationMs: 0,
};

// NOTE: NSAI-5705 — a usage row may carry no endpoint type at all (activity
// types such as `prompt_improvement` record none), and SQL `IN` never matches
// NULL. Those rows were therefore unreachable through the Endpoint filter: with
// every real option ticked the list still came back short, and the summary
// tiles under-reported with nothing on screen saying anything was dropped.
//
// This sentinel gives them a value to be selected BY. It is deliberately not a
// member of ETokenUsageEndpointType — that enum mirrors what the column can
// actually hold, and "no value" is not one of them — so it lives beside the
// enum rather than inside it, and the server maps it to `IS NULL`.
// Keep the literal in sync with TOKEN_USAGE_ENDPOINT_TYPE_NOT_SET in
// server/src/token-usage/token-usage.constant.ts.
export const TOKEN_USAGE_ENDPOINT_TYPE_NOT_SET = "not_set" as const;

// NOTE: What the Endpoint filter may hold — every real endpoint type plus the
// "recorded without one" sentinel above.
export type TTokenUsageEndpointFilterValue =
  | ETokenUsageEndpointType
  | typeof TOKEN_USAGE_ENDPOINT_TYPE_NOT_SET;

// NOTE: Every value the Endpoint filter offers, in the order it renders. Shared
// so web and mobile cannot drift on which options exist — a filter that offers
// different options per platform is how NSAI-5705 would come back.
export const TOKEN_USAGE_ENDPOINT_FILTER_VALUES: TTokenUsageEndpointFilterValue[] =
  [
    ...Object.values(ETokenUsageEndpointType),
    TOKEN_USAGE_ENDPOINT_TYPE_NOT_SET,
  ];

export const TOKEN_USAGE_PERCENT_MAX = 100;

// NOTE: Severity buckets for a crossed budget, in percent of the limit.
export const TOKEN_USAGE_TIER_CRITICAL_MIN_PERCENT = 95;
export const TOKEN_USAGE_TIER_ELEVATED_MIN_PERCENT = 90;

// NOTE: Ordered largest-first so the first matching unit wins.
export const COMPACT_TOKEN_UNITS = [
  { threshold: 1_000_000_000, suffix: "B" },
  { threshold: 1_000_000, suffix: "M" },
  { threshold: 1_000, suffix: "K" },
] as const;

// NOTE: What one decimal place looks like once it has rounded up into the next
// unit, e.g. 999_999 / 1_000 → "1000".
export const COMPACT_TOKEN_MANTISSA_ROUNDED_UP = "1000" as const;

// NOTE: Micro-dollars — mirrors TOKEN_COST_DECIMALS in the server's
// token-usage.constant.ts, which is the precision every cost on this contract is
// already rounded to. Declared here rather than imported because this package
// cannot reach into the server.
export const TOKEN_COST_DECIMALS = 6;

// NOTE: NSAI-5036 — the budget windows an owner can pick, SHORTEST FIRST so the
// select reads as an escalating scale. Shared rather than duplicated per client:
// web renders it as the period dropdown and both clients map the saved value to
// a reset caption, so a new period must not require finding every copy.
//
// NOTE: `as const satisfies` rather than a `readonly TTokenLimitPeriod[]`
// annotation — the annotation would widen this to an array and zod's `z.enum`
// needs the literal tuple. `satisfies` still fails the build if a member is not
// a TTokenLimitPeriod, so the two cannot drift.
export const TOKEN_LIMIT_PERIODS = [
  "week",
  "month",
  "quarter",
  "half_year",
  "year",
] as const satisfies readonly TTokenLimitPeriod[];

// NOTE: Period -> the `settings` i18n key naming it in the period picker.
//
// NOTE: `as const satisfies` again — web's `t()` is typed against the generated
// key union, so a `Record<..., string>` annotation would widen these to `string`
// and every lookup would fail to type-check at the call site. `satisfies` still
// requires an entry for every period.
export const TOKEN_LIMIT_PERIOD_LABEL_KEYS = {
  week: "usageLimits.periodWeek",
  month: "usageLimits.periodMonth",
  quarter: "usageLimits.periodQuarter",
  half_year: "usageLimits.periodHalfYear",
  year: "usageLimits.periodYear",
} as const satisfies Record<TTokenLimitPeriod, string>;

// NOTE: Period -> the `settings` i18n key for the "Resets ... · next reset
// {{date}}" caption under the budget summary. A full map rather than the old
// week/month ternary, which silently captioned every longer window as monthly.
export const TOKEN_LIMIT_PERIOD_RESET_LABEL_KEYS = {
  week: "organizationTabs.usageLimitsOverview.resetsWeekly",
  month: "organizationTabs.usageLimitsOverview.resetsMonthly",
  quarter: "organizationTabs.usageLimitsOverview.resetsQuarterly",
  half_year: "organizationTabs.usageLimitsOverview.resetsHalfYearly",
  year: "organizationTabs.usageLimitsOverview.resetsYearly",
} as const satisfies Record<TTokenLimitPeriod, string>;
